import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight, Minus, AlertCircle } from 'lucide-react';
import { EvaluationAnalytics } from '../types/evaluation';
import { EvaluationApiService } from '../services/evaluationApi';
import { AppLayout } from './AppLayout';
import { AIScoreStatsMap, AIScoreStatsEntry, ScoreDistribution, calculateAIScoreStats } from '../utils/aiScoreUtils';

interface MatchedProvider {
  sourceProviderId: string;
  newProviderId: string;
  name: string;
  matchType: 'configId' | 'name';
}

interface UnmatchedProvider {
  providerId: string;
  name: string;
  side: 'source' | 'new';
}

const BREAKDOWN_METRICS = [
  { key: 'accuracy' as const, label: 'Accuracy', weight: '40%', color: 'bg-blue-500', lightColor: 'bg-blue-100' },
  { key: 'citationQuality' as const, label: 'Citation Quality', weight: '25%', color: 'bg-green-500', lightColor: 'bg-green-100' },
  { key: 'completeness' as const, label: 'Completeness', weight: '20%', color: 'bg-yellow-500', lightColor: 'bg-yellow-100' },
  { key: 'legalQuality' as const, label: 'Legal Quality', weight: '15%', color: 'bg-purple-500', lightColor: 'bg-purple-100' },
];

export const AIComparisonView = () => {
  const { testId, sourceTestId } = useParams<{ testId: string; sourceTestId: string }>();
  const navigate = useNavigate();

  const [sourceAnalytics, setSourceAnalytics] = useState<EvaluationAnalytics | null>(null);
  const [newAnalytics, setNewAnalytics] = useState<EvaluationAnalytics | null>(null);
  const [sourceStats, setSourceStats] = useState<AIScoreStatsMap>({});
  const [newStats, setNewStats] = useState<AIScoreStatsMap>({});
  const [sourceTestName, setSourceTestName] = useState<string>('');
  const [newTestName, setNewTestName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [sourceDeleted, setSourceDeleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedDistributions, setExpandedDistributions] = useState<Set<string>>(new Set());

  const evaluationApi = EvaluationApiService.getInstance();

  useEffect(() => {
    if (!testId || !sourceTestId) return;
    loadComparisonData();
  }, [testId, sourceTestId]);

  const loadComparisonData = async () => {
    if (!testId || !sourceTestId) return;

    try {
      setIsLoading(true);

      // Load test names
      const [newTest, sourceTest] = await Promise.all([
        evaluationApi.getEvaluationTest(testId),
        evaluationApi.getEvaluationTest(sourceTestId).catch(() => null),
      ]);

      setNewTestName(newTest?.name || 'New Test');

      if (!sourceTest) {
        setSourceDeleted(true);
        setIsLoading(false);
        return;
      }
      setSourceTestName(sourceTest.name);

      // Load analytics for both tests
      const [newAnalyticsData, sourceAnalyticsData] = await Promise.all([
        evaluationApi.getAnalytics(testId),
        evaluationApi.getAnalytics(sourceTestId),
      ]);

      setNewAnalytics(newAnalyticsData);
      setSourceAnalytics(sourceAnalyticsData);

      // Load AI score stats for both
      const [newExecs, sourceExecs] = await Promise.all([
        evaluationApi.listExecutions(testId),
        evaluationApi.listExecutions(sourceTestId),
      ]);

      const [newScores, sourceScores] = await Promise.all([
        newExecs.length > 0
          ? evaluationApi.getAIScoresBatch({ evaluationExecutionIds: newExecs.map(e => e.id) })
          : Promise.resolve({} as Record<string, any[]>),
        sourceExecs.length > 0
          ? evaluationApi.getAIScoresBatch({ evaluationExecutionIds: sourceExecs.map(e => e.id) })
          : Promise.resolve({} as Record<string, any[]>),
      ]);

      const newResult = calculateAIScoreStats(newAnalyticsData, newExecs, newScores);
      const sourceResult = calculateAIScoreStats(sourceAnalyticsData, sourceExecs, sourceScores);

      setNewStats(newResult.stats);
      setSourceStats(sourceResult.stats);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load comparison data:', err);
      setError(err.message || 'Failed to load comparison data');
    } finally {
      setIsLoading(false);
    }
  };

  const matchProviders = (): { matched: MatchedProvider[]; unmatched: UnmatchedProvider[] } => {
    if (!sourceAnalytics || !newAnalytics) return { matched: [], unmatched: [] };

    const sourceProviders = sourceAnalytics.providerMetrics;
    const newProviders = newAnalytics.providerMetrics;
    const matched: MatchedProvider[] = [];
    const matchedSourceIds = new Set<string>();
    const matchedNewIds = new Set<string>();

    // Pass 1: match by config ID (providerId)
    for (const sp of sourceProviders) {
      const np = newProviders.find(n => n.providerId === sp.providerId && !matchedNewIds.has(n.providerId));
      if (np) {
        matched.push({
          sourceProviderId: sp.providerId,
          newProviderId: np.providerId,
          name: sp.providerName,
          matchType: 'configId',
        });
        matchedSourceIds.add(sp.providerId);
        matchedNewIds.add(np.providerId);
      }
    }

    // Pass 2: match by name
    for (const sp of sourceProviders) {
      if (matchedSourceIds.has(sp.providerId)) continue;
      const np = newProviders.find(n => !matchedNewIds.has(n.providerId) && n.providerName === sp.providerName);
      if (np) {
        matched.push({
          sourceProviderId: sp.providerId,
          newProviderId: np.providerId,
          name: sp.providerName,
          matchType: 'name',
        });
        matchedSourceIds.add(sp.providerId);
        matchedNewIds.add(np.providerId);
      }
    }

    // Unmatched
    const unmatched: UnmatchedProvider[] = [];
    for (const sp of sourceProviders) {
      if (!matchedSourceIds.has(sp.providerId)) {
        unmatched.push({ providerId: sp.providerId, name: sp.providerName, side: 'source' });
      }
    }
    for (const np of newProviders) {
      if (!matchedNewIds.has(np.providerId)) {
        unmatched.push({ providerId: np.providerId, name: np.providerName, side: 'new' });
      }
    }

    return { matched, unmatched };
  };

  const toggleDistribution = (key: string) => {
    setExpandedDistributions(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderDelta = (sourceVal: number, newVal: number) => {
    const diff = newVal - sourceVal;
    const pctDiff = (diff * 100).toFixed(1);
    if (Math.abs(diff) < 0.005) {
      return <Minus className="w-4 h-4 text-gray-400 inline" />;
    }
    if (diff > 0) {
      return (
        <span className="inline-flex items-center text-green-600 text-xs font-medium">
          <ArrowUpRight className="w-3.5 h-3.5" />+{pctDiff}%
        </span>
      );
    }
    return (
      <span className="inline-flex items-center text-red-600 text-xs font-medium">
        <ArrowDownRight className="w-3.5 h-3.5" />{pctDiff}%
      </span>
    );
  };

  const renderBreakdownBar = (value: number, color: string, lightColor: string) => (
    <div className={`w-full ${lightColor} rounded-full h-5 overflow-hidden`}>
      <div
        className={`${color} h-5 transition-all duration-300 flex items-center justify-end pr-2`}
        style={{ width: `${value * 100}%` }}
      >
        {value >= 0.15 && (
          <span className="text-xs font-medium text-white">{(value * 100).toFixed(1)}%</span>
        )}
      </div>
    </div>
  );

  const renderDistributionBar = (distribution: ScoreDistribution, labels: string[]) => {
    const maxCount = Math.max(...Object.values(distribution), 1);
    const keys = Object.keys(distribution).map(Number).sort((a, b) => a - b);

    return (
      <div className="space-y-1">
        {keys.map((key, i) => {
          const count = distribution[key] || 0;
          const pct = (count / maxCount) * 100;
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600 w-12 text-right">{labels[i] ?? key}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                <div
                  className={`${count > 0 ? 'bg-blue-500' : 'bg-gray-200'} h-full transition-all duration-300 flex items-center justify-end pr-1`}
                  style={{ width: `${pct}%` }}
                >
                  {count > 0 && <span className="text-xs font-medium text-white">{count}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const OVERALL_LABELS = Array.from({ length: 10 }, (_, i) => String(i + 1));
  const BREAKDOWN_LABELS = ['0-10%', '10-20%', '20-30%', '30-40%', '40-50%', '50-60%', '60-70%', '70-80%', '80-90%', '90-100%'];

  const renderAIScoreDistributions = (stats: AIScoreStatsEntry) => {
    return (
      <div className="space-y-4">
        {/* Overall AI Score */}
        <div>
          <div className="text-xs font-semibold text-gray-700 mb-1">Overall AI Score (1–10)</div>
          {renderDistributionBar(stats.overallDistribution, OVERALL_LABELS)}
        </div>
        {/* Per-category breakdowns */}
        {BREAKDOWN_METRICS.map(metric => (
          <div key={metric.key}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2.5 h-2.5 rounded-full ${metric.color}`} />
              <span className="text-xs font-semibold text-gray-700">{metric.label} (0–100%)</span>
            </div>
            {renderDistributionBar(stats.breakdownDistributions[metric.key], BREAKDOWN_LABELS)}
          </div>
        ))}
      </div>
    );
  };

  const renderSummary = () => {
    const { matched } = matchProviders();
    if (matched.length === 0) return null;

    let totalSourceAvg = 0;
    let totalNewAvg = 0;
    const categoryChanges = { accuracy: 0, citationQuality: 0, completeness: 0, legalQuality: 0 };
    const categoryCounts = { accuracy: 0, citationQuality: 0, completeness: 0, legalQuality: 0 };
    let validPairs = 0;

    for (const m of matched) {
      const ss = sourceStats[m.sourceProviderId];
      const ns = newStats[m.newProviderId];
      if (!ss || !ns || ss.count === 0 || ns.count === 0) continue;
      validPairs++;
      totalSourceAvg += ss.average;
      totalNewAvg += ns.average;
      for (const key of Object.keys(categoryChanges) as Array<keyof typeof categoryChanges>) {
        categoryChanges[key] += ns.breakdownAverages[key] - ss.breakdownAverages[key];
        categoryCounts[key]++;
      }
    }

    if (validPairs === 0) return null;

    const overallChange = (totalNewAvg / validPairs) - (totalSourceAvg / validPairs);
    let improved = 0;
    const total = Object.keys(categoryChanges).length;
    for (const key of Object.keys(categoryChanges) as Array<keyof typeof categoryChanges>) {
      if (categoryCounts[key] > 0 && categoryChanges[key] / categoryCounts[key] > 0.005) improved++;
    }

    return (
      <div data-flow="summary" className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Comparison Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Overall */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs font-medium text-gray-600 mb-1">Overall AI Score</div>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-gray-900">
                {overallChange >= 0 ? '+' : ''}{overallChange.toFixed(2)}
              </span>
              {overallChange > 0.05 ? (
                <ArrowUpRight className="w-5 h-5 text-green-600" />
              ) : overallChange < -0.05 ? (
                <ArrowDownRight className="w-5 h-5 text-red-600" />
              ) : (
                <Minus className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* Per-category */}
          {BREAKDOWN_METRICS.map(metric => {
            const count = categoryCounts[metric.key];
            const change = count > 0 ? categoryChanges[metric.key] / count : 0;
            return (
              <div key={metric.key} className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-600 mb-1">{metric.label}</div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-gray-900">
                    {change >= 0 ? '+' : ''}{(change * 100).toFixed(1)}%
                  </span>
                  {renderDelta(0, change)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Verdict */}
        <div className="mt-3 text-sm font-medium text-gray-700">
          Improved in {improved}/{total} categories
        </div>
      </div>
    );
  };

  const renderMatchedRow = (m: MatchedProvider) => {
    const ss = sourceStats[m.sourceProviderId];
    const ns = newStats[m.newProviderId];
    const hasSourceAI = ss && ss.count > 0;
    const hasNewAI = ns && ns.count > 0;
    const distKey = `${m.sourceProviderId}-${m.newProviderId}`;
    const isExpanded = expandedDistributions.has(distKey);

    // Get analytics metrics for result type comparison
    const sourceMetrics = sourceAnalytics?.providerMetrics.find(p => p.providerId === m.sourceProviderId);
    const newMetrics = newAnalytics?.providerMetrics.find(p => p.providerId === m.newProviderId);

    return (
      <div key={distKey} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
        {/* Provider Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">{m.name}</h4>
            <span className="text-xs text-gray-400">Matched by {m.matchType === 'configId' ? 'config ID' : 'name'}</span>
          </div>
          {hasSourceAI && hasNewAI && (
            <div className="text-right">
              <div className="text-sm text-gray-500">Overall AI Score</div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{ss.average.toFixed(2)}</span>
                <span className="text-gray-400">→</span>
                <span className="font-bold text-gray-900">{ns.average.toFixed(2)}</span>
                {renderDelta(ss.average / 10, ns.average / 10)}
              </div>
            </div>
          )}
        </div>

        {/* Side-by-side breakdown */}
        {hasSourceAI && hasNewAI ? (
          <div className="grid grid-cols-2 gap-6">
            {/* Source (left) */}
            <div>
              <div className="text-sm font-medium text-gray-500 mb-2">Source Test</div>
              <div className="space-y-3">
                {BREAKDOWN_METRICS.map(metric => (
                  <div key={metric.key}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-700 font-medium">{metric.label}</span>
                      <span className="font-semibold text-gray-900">
                        {(ss.breakdownAverages[metric.key] * 100).toFixed(1)}%
                      </span>
                    </div>
                    {renderBreakdownBar(ss.breakdownAverages[metric.key], metric.color, metric.lightColor)}
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-500">Based on {ss.count} scored execution{ss.count !== 1 ? 's' : ''}</div>
            </div>

            {/* New (right) */}
            <div>
              <div className="text-sm font-medium text-gray-500 mb-2">New Test</div>
              <div className="space-y-3">
                {BREAKDOWN_METRICS.map(metric => (
                  <div key={metric.key}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700 font-medium">{metric.label}</span>
                        {renderDelta(ss.breakdownAverages[metric.key], ns.breakdownAverages[metric.key])}
                      </div>
                      <span className="font-semibold text-gray-900">
                        {(ns.breakdownAverages[metric.key] * 100).toFixed(1)}%
                      </span>
                    </div>
                    {renderBreakdownBar(ns.breakdownAverages[metric.key], metric.color, metric.lightColor)}
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-500">Based on {ns.count} scored execution{ns.count !== 1 ? 's' : ''}</div>
            </div>
          </div>
        ) : (hasSourceAI || hasNewAI) ? (
          <div className="grid grid-cols-2 gap-6">
            {/* Source (left) */}
            <div>
              <div className="text-sm font-medium text-gray-500 mb-2">Source Test</div>
              {hasSourceAI ? (
                <>
                  <div className="space-y-3">
                    {BREAKDOWN_METRICS.map(metric => (
                      <div key={metric.key}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-700 font-medium">{metric.label}</span>
                          <span className="font-semibold text-gray-900">
                            {(ss.breakdownAverages[metric.key] * 100).toFixed(1)}%
                          </span>
                        </div>
                        {renderBreakdownBar(ss.breakdownAverages[metric.key], metric.color, metric.lightColor)}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">Based on {ss.count} scored execution{ss.count !== 1 ? 's' : ''}</div>
                </>
              ) : (
                <div className="text-sm text-gray-400 text-center py-8">No AI scores</div>
              )}
            </div>

            {/* New (right) */}
            <div>
              <div className="text-sm font-medium text-gray-500 mb-2">New Test</div>
              {hasNewAI ? (
                <>
                  <div className="space-y-3">
                    {BREAKDOWN_METRICS.map(metric => (
                      <div key={metric.key}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-700 font-medium">{metric.label}</span>
                          <span className="font-semibold text-gray-900">
                            {(ns.breakdownAverages[metric.key] * 100).toFixed(1)}%
                          </span>
                        </div>
                        {renderBreakdownBar(ns.breakdownAverages[metric.key], metric.color, metric.lightColor)}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">Based on {ns.count} scored execution{ns.count !== 1 ? 's' : ''}</div>
                </>
              ) : (
                <div className="text-sm text-gray-400 text-center py-8">No AI scores</div>
              )}
            </div>
          </div>
        ) : null}

        {/* Result Type Distribution - always show if analytics data exists */}
        {(sourceMetrics || newMetrics) && (
          <div className={`${(hasSourceAI || hasNewAI) ? 'mt-4 pt-4 border-t border-gray-200' : ''}`}>
            <div className="text-sm font-semibold text-gray-700 mb-3">Result Types</div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                {!hasSourceAI && !hasNewAI && <div className="text-xs font-medium text-gray-500 mb-2">Source Test</div>}
                {sourceMetrics && Object.keys(sourceMetrics.resultTypeDistribution).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(sourceMetrics.resultTypeDistribution).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{status}</span>
                        <span className="font-medium text-gray-900">{count}</span>
                      </div>
                    ))}
                    <div className="text-xs text-gray-400 mt-1">{sourceMetrics.totalScores} user score{sourceMetrics.totalScores !== 1 ? 's' : ''}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">No executions</div>
                )}
              </div>
              <div>
                {!hasSourceAI && !hasNewAI && <div className="text-xs font-medium text-gray-500 mb-2">New Test</div>}
                {newMetrics && Object.keys(newMetrics.resultTypeDistribution).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(newMetrics.resultTypeDistribution).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{status}</span>
                        <span className="font-medium text-gray-900">{count}</span>
                      </div>
                    ))}
                    <div className="text-xs text-gray-400 mt-1">{newMetrics.totalScores} user score{newMetrics.totalScores !== 1 ? 's' : ''}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">No executions</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Collapsible Score Distribution */}
        {(hasSourceAI || hasNewAI) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => toggleDistribution(distKey)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Score Distributions
            </button>
            {isExpanded && hasSourceAI && hasNewAI && (
              <div data-flow="score-distribution" className="grid grid-cols-2 gap-6 mt-3">
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2">Source Test</div>
                  {renderAIScoreDistributions(ss)}
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2">New Test</div>
                  {renderAIScoreDistributions(ns)}
                </div>
              </div>
            )}
            {isExpanded && hasSourceAI && !hasNewAI && (
              <div className="mt-3">
                <div className="text-xs font-medium text-gray-500 mb-2">Source Test</div>
                {renderAIScoreDistributions(ss)}
              </div>
            )}
            {isExpanded && !hasSourceAI && hasNewAI && (
              <div className="mt-3">
                <div className="text-xs font-medium text-gray-500 mb-2">New Test</div>
                {renderAIScoreDistributions(ns)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderUnmatchedProvider = (u: UnmatchedProvider) => {
    const stats = u.side === 'source' ? sourceStats[u.providerId] : newStats[u.providerId];
    const hasStats = stats && stats.count > 0;
    const distKey = `unmatched-${u.providerId}`;
    const isExpanded = expandedDistributions.has(distKey);

    return (
      <div key={u.providerId} className="border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-base font-semibold text-gray-900">{u.name}</h4>
            <span className={`text-xs px-2 py-0.5 rounded ${u.side === 'source' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
              {u.side === 'source' ? 'Source only' : 'New only'}
            </span>
          </div>
          {hasStats && (
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{stats.average.toFixed(2)}</div>
              <div className="text-xs text-gray-500">AI Avg Score</div>
            </div>
          )}
        </div>

        {hasStats && (
          <div className="space-y-3">
            {BREAKDOWN_METRICS.map(metric => (
              <div key={metric.key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700 font-medium">{metric.label}</span>
                  <span className="font-semibold text-gray-900">
                    {(stats.breakdownAverages[metric.key] * 100).toFixed(1)}%
                  </span>
                </div>
                {renderBreakdownBar(stats.breakdownAverages[metric.key], metric.color, metric.lightColor)}
              </div>
            ))}
            <div className="text-xs text-gray-500">Based on {stats.count} scored execution{stats.count !== 1 ? 's' : ''}</div>
          </div>
        )}

        {/* Collapsible Score Distribution */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={() => toggleDistribution(distKey)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Score Distributions
          </button>
          {isExpanded && hasStats && (
            <div data-flow="score-distribution" className="mt-3">
              {renderAIScoreDistributions(stats)}
            </div>
          )}
          {isExpanded && !hasStats && (
            <div className="text-sm text-gray-500 mt-3">No AI scores available.</div>
          )}
        </div>
      </div>
    );
  };

  // --- Render states ---

  if (isLoading) {
    return (
      <AppLayout>
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <ArrowLeft className="w-4 h-4" />Back
            </button>
          </div>
        </div>
        <div data-component="AIComparisonView" data-flow="loading" className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200 m-6">
          <div className="text-gray-500">Loading comparison data...</div>
        </div>
      </AppLayout>
    );
  }

  if (sourceDeleted) {
    return (
      <AppLayout>
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <button onClick={() => navigate(`/opinions/evaluation/${testId}/analytics`)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <ArrowLeft className="w-4 h-4" />Back to Analytics
            </button>
          </div>
        </div>
        <div data-component="AIComparisonView" data-flow="source-deleted" className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200 m-6">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-orange-400 mx-auto mb-3" />
            <p className="font-medium text-gray-900">Source test no longer available</p>
            <p className="text-sm text-gray-500 mt-1">The source evaluation test may have been deleted.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <ArrowLeft className="w-4 h-4" />Back
            </button>
          </div>
        </div>
        <div data-component="AIComparisonView" data-flow="error" className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200 m-6">
          <div className="text-center text-red-600">
            <AlertCircle className="w-12 h-12 mx-auto mb-3" />
            <p className="font-medium">Error loading comparison</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const { matched, unmatched } = matchProviders();

  return (
    <AppLayout>
      {/* Page Actions Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{sourceTestName}</span>
            <span className="mx-2 text-gray-400">vs</span>
            <span className="font-medium">{newTestName}</span>
          </div>
          <button onClick={() => navigate(`/opinions/evaluation/${testId}/analytics`)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-4 h-4" />Back to Analytics
          </button>
        </div>
      </div>

      <div data-component="AIComparisonView" data-flow="comparison" className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          {/* Summary */}
          {renderSummary()}

          {/* Matched Providers */}
          {matched.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Provider Comparison</h3>
              <div className="space-y-6">
                {matched.map(renderMatchedRow)}
              </div>
            </div>
          )}

          {/* Unmatched Providers */}
          {unmatched.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Unmatched Providers</h3>
              <p className="text-sm text-gray-500 mb-4">These providers exist in only one of the tests and could not be matched.</p>
              <div className="space-y-4">
                {unmatched.map(renderUnmatchedProvider)}
              </div>
            </div>
          )}

          {matched.length === 0 && unmatched.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No provider data available for comparison.
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};
